import type { PlannedPostCreate, PlanningProposal } from '../../types/api';
import { campaignsApi, plannedPostsApi, saveContentGoals } from '../calendar/calendarApi';
import { monthOf } from '../calendar/calendarDates';

/**
 * Turns one proposal into a real record.
 *
 * Applying writes through the calendar's own endpoints — a proposal the user
 * accepts is an ordinary planned post, campaign or set of monthly goals, and
 * there is no planner-owned copy of any of them. The assistant only records the
 * decision afterwards, through `setAssistantProposalStatus`.
 *
 * Answers the created row's id, which the caller files as the mark's
 * `resource_id`. Goals are a per-month singleton, so the PUT is an upsert and a
 * *partial merge*: fields the proposal leaves out keep the values already set.
 *
 * Lives outside `ProposalCard` because "Apply all" runs the same write from the
 * message level, and two copies of it would be two places for the shape of a
 * planned post to drift.
 */
export async function applyProposal(
  businessId: string,
  proposal: PlanningProposal,
): Promise<string> {
  switch (proposal.type) {
    case 'planned_post': {
      // The create body carries every field; the proposal only carries the ones
      // the planner had an opinion about.
      const body: PlannedPostCreate = {
        campaign_id: proposal.campaign_id ?? null,
        title: proposal.title,
        brief: proposal.brief ?? null,
        platform: proposal.platform ?? null,
        post_type: proposal.post_type ?? null,
        // The planner names a purpose per post (the worker already dropped any
        // key outside the registry); an older proposal without one leaves it
        // to the model, as every plan did before intents existed.
        intents: proposal.intents ?? [],
        scheduled_on: proposal.scheduled_on,
      };
      const created = await plannedPostsApi.create(businessId, body);
      return created.id;
    }

    case 'campaign': {
      const created = await campaignsApi.create(businessId, {
        name: proposal.name,
        color: proposal.color,
        description: proposal.description ?? null,
        starts_on: proposal.starts_on ?? null,
        ends_on: proposal.ends_on ?? null,
      });
      return created.id;
    }

    case 'content_goals': {
      // The proposal carries its own month, so applying a September suggestion
      // from an August conversation lands on September.
      const saved = await saveContentGoals(businessId, monthOf(proposal.month), {
        ...(proposal.focus === undefined ? {} : { focus: proposal.focus }),
        ...(proposal.cadence_targets === undefined
          ? {}
          : { cadence_targets: proposal.cadence_targets }),
        ...(proposal.metric_targets === undefined
          ? {}
          : { metric_targets: proposal.metric_targets }),
      });
      return saved.id;
    }
  }
}
